import { FC, useEffect } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import styled from 'styled-components'
import { number, object, string } from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { Box, Button, Dropdown, Fieldset, RadioGroup } from '@mrshmllw/smores-react'

import { AdditionalDriverItem, ConvictionItem, usePersistentState } from '../../../../state'
import { generateAdditionalDriverNames } from '../../utils'
import { POLICY_HOLDER_ID } from 'directFlow/constants'
import { requiredMessage } from 'directFlow/state/config/content'
import { Page } from 'directFlow/types'

import { ListItemBEResponse } from 'types'
import { monthOptions } from 'app/data'
import { useEventTracking } from 'utils'

const currentYear = new Date().getFullYear()
const yearList = [...Array(6)].map((_, index) => {
  const year = `${currentYear - index}`
  return {
    label: year,
    value: year,
  }
})

const getDefaultValues = (hasAdditionalDrivers: boolean): Partial<ConvictionItem> => ({
  driverId: hasAdditionalDrivers ? undefined : POLICY_HOLDER_ID,
  month: '',
  year: undefined,
  type: '',
})

export const convictionsModalSchema = object({
  driverId: string().required(requiredMessage),
  month: string().required(requiredMessage),
  year: number().required(requiredMessage),
  type: string().required(requiredMessage),
})

const resolver = yupResolver(convictionsModalSchema)

type AddConvictionFormProps = {
  options: ListItemBEResponse[]
  initialState?: ConvictionItem
  onSubmit: (value: ConvictionItem) => void
}

export const AddConvictionForm: FC<AddConvictionFormProps> = ({
  options,
  initialState,
  onSubmit,
}) => {
  const persistentState = usePersistentState()
  const additionalDrivers = (persistentState.getPageState(Page.DRIVERS)?.additionalDrivers?.list ||
    []) as AdditionalDriverItem[]
  const hasAdditionalDrivers = additionalDrivers.length > 0
  const additionalDriverNames =
    additionalDrivers.length > 0 ? generateAdditionalDriverNames(additionalDrivers) : null

  const { control, handleSubmit, trigger } = useForm<ConvictionItem>({
    defaultValues: initialState || getDefaultValues(hasAdditionalDrivers),
    resolver,
  })

  useEffect(() => {
    if (initialState !== undefined) {
      trigger()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger])

  const [month, year] = useWatch({
    control,
    name: ['month', 'year'],
  })

  const track = useEventTracking()

  return (
    <Wrapper>
      {additionalDriverNames && (
        <Controller
          name="driverId"
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <RadioGroup
              label="Which driver would you like to add a conviction for?"
              data-testid="additionalDriverName"
              displayType="vertical-card"
              renderAsTitle
              value={value}
              options={additionalDriverNames}
              onChange={(value) => {
                onChange(value)
                track('ConvictionAdditionalDriverName', { value })
              }}
              error={!!error}
              errorMsg={error?.message}
              completed={value !== undefined}
            />
          )}
        />
      )}
      <Fieldset label="When did you get this conviction?" completed={!!(month && year)}>
        <Controller
          name="month"
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Dropdown
              label="Month"
              placeholder="Select a month"
              showDefaultOption
              customDefaultOption="Select month"
              list={monthOptions}
              onSelect={onChange}
              value={value}
              error={error?.message !== undefined}
              errorMsg={error?.message}
              mb="12px"
              data-testid="convictionMonth"
            />
          )}
        />

        <Controller
          name="year"
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Dropdown
              label="Year"
              placeholder="Select a year"
              showDefaultOption
              customDefaultOption="Select year"
              list={yearList}
              onSelect={(year) => onChange(Number(year))}
              value={value?.toString()}
              error={error?.message !== undefined}
              errorMsg={error?.message}
              data-testid="convictionYear"
            />
          )}
        />
      </Fieldset>
      <Controller
        name="type"
        control={control}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <Dropdown
            renderAsTitle
            label="What type of conviction was it?"
            assistiveText="For example, was it a speeding conviction? Another type of conviction?"
            placeholder="Select conviction type"
            showDefaultOption
            customDefaultOption="Select conviction type"
            list={options.map((option) => ({
              label: option.name,
              value: option.value,
            }))}
            onSelect={onChange}
            value={value}
            error={error?.message !== undefined}
            errorMsg={error?.message}
            completed={Boolean(value)}
            data-testid="convictionType"
          />
        )}
      />

      <Button primary onClick={handleSubmit(onSubmit)} data-testid="saveConviction">
        Save conviction
      </Button>
    </Wrapper>
  )
}

const Wrapper = styled(Box)`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 48px;
`
